import { yupResolver } from '@hookform/resolvers/yup';
import { PayrollCycle } from '@zorro/clients';
import { Button } from '@zorro/design-system/v2/Button';
import { Card } from '@zorro/design-system/v2/Card';
import { useDrawerFooter } from '@zorro/design-system/v2/Drawer';
import { Link } from '@zorro/design-system/v2/Link';
import { TabButtonGroup } from '@zorro/design-system/v2/TabButtonGroup';
import { Title } from '@zorro/design-system/v2/Title';
import { formatDateISO, parseDate } from '@zorro/shared/formatters';
import {
  DateInput,
  FormErrorMessage,
  Grid,
  InputWrapper,
  LabelWithTooltip,
  Stack,
  Text,
} from '@zorro/web/design-system';
import {
  FormFooter,
  InformationBoxComponent,
  PayrollCycleInput,
  PayrollFormData,
  PayrollReportMode,
  calculatePayPeriodEndDate,
  payrollFormSchema,
  useAnalytics,
} from '@zorro/web/shared-ui';
import { getPayrollCycleLabel, useForm } from '@zorro/web/ui-utils';
import { useMemo } from 'react';
import { Controller } from 'react-hook-form';

type PayrollReportFormProps = {
  isEditMode?: boolean;
  currentReportValues?: PayrollFormData;
  employerPayrollCycle: PayrollCycle;
  onGenerateReportClick: (selectedPayroll: PayrollFormData) => void;
  closeDrawer?: () => void;
};

export const PayrollReportForm = ({
  isEditMode = false,
  currentReportValues,
  employerPayrollCycle,
  onGenerateReportClick,
  closeDrawer = () => undefined,
}: PayrollReportFormProps) => {
  const { control, getValues, setValue, formState, watch } =
    useForm<PayrollFormData>({
      mode: 'all',
      resolver: yupResolver(payrollFormSchema),
      defaultValues: currentReportValues || {
        reportMode: PayrollReportMode.BASIC,
        payrollCycle: employerPayrollCycle,
      },
    });

  const { isValid, errors } = formState;
  const { sendEvent } = useAnalytics();

  const isAdvancedMode = watch('reportMode') === PayrollReportMode.ADVANCED;
  const selectedPayrollCycle = watch('payrollCycle');
  const selectedStartDate = watch('payPeriodStartDate');
  const isPayPeriodDefined =
    Boolean(watch('payPeriodStartDate')) && Boolean(watch('payPeriodEndDate'));

  const handleAutomaticEndDateChange = (
    isAdvancedMode: boolean,
    selectedPayrollCycle: PayrollCycle,
    selectedStartDate: Date
  ) => {
    const payrollCycle = isAdvancedMode
      ? selectedPayrollCycle
      : employerPayrollCycle;
    if (selectedStartDate) {
      const endDate = calculatePayPeriodEndDate(
        payrollCycle,
        selectedStartDate
      );
      setValue('payPeriodEndDate', endDate, { shouldValidate: true });
    }
  };

  const handleDefaultIndicateChangesSinceUpdate = (
    isPayPeriodDefined: boolean,
    selectedStartDate: Date
  ) => {
    if (!selectedStartDate) {
      return;
    }
    const defaultIndicateChangesSince = isPayPeriodDefined
      ? selectedStartDate
      : null;

    setValue('indicateChangesSince', defaultIndicateChangesSince, {
      shouldValidate: true,
    });
  };

  const drawerFooter = useMemo(
    () =>
      isEditMode ? (
        <FormFooter
          withPaper={false}
          primaryLabel="Update report"
          primaryButtonProps={{
            onClick: () => {
              onGenerateReportClick(getValues());
              closeDrawer();
            },
            disabled: !isValid,
          }}
          secondaryLabel="Cancel"
          secondaryButtonProps={{ onClick: closeDrawer }}
        />
      ) : null,
    [isEditMode, onGenerateReportClick, getValues, isValid, closeDrawer]
  );
  useDrawerFooter(drawerFooter);

  return (
    <Stack gap="2rem" w={isEditMode ? '42rem' : '63rem'}>
      {!isEditMode && <Title>Payroll report</Title>}

      <Grid columns={30}>
        <Grid.Col span={14}>
          <Controller
            control={control}
            name="reportMode"
            render={({ field: { onChange, ...rest } }) => (
              <InputWrapper w="15rem">
                <TabButtonGroup
                  {...rest}
                  onChange={async (reportMode) => {
                    onChange(reportMode);
                    setValue('payrollCycle', employerPayrollCycle);
                    const isAdvancedMode =
                      reportMode === PayrollReportMode.ADVANCED;
                    handleAutomaticEndDateChange(
                      isAdvancedMode,
                      selectedPayrollCycle,
                      selectedStartDate
                    );
                    if (selectedStartDate) {
                      handleDefaultIndicateChangesSinceUpdate(
                        isPayPeriodDefined,
                        selectedStartDate
                      );
                    }
                    await sendEvent(
                      isAdvancedMode ? 'change_to_advanced' : 'change_to_basic'
                    );
                  }}
                >
                  <TabButtonGroup.List grow>
                    <TabButtonGroup.Tab value={PayrollReportMode.BASIC}>
                      Basic
                    </TabButtonGroup.Tab>
                    <TabButtonGroup.Tab value={PayrollReportMode.ADVANCED}>
                      Advanced
                    </TabButtonGroup.Tab>
                  </TabButtonGroup.List>
                </TabButtonGroup>
              </InputWrapper>
            )}
          />
          <FormErrorMessage errors={errors} fieldName="reportMode" />
        </Grid.Col>
      </Grid>

      <Card
        p={isEditMode ? 0 : 'xl'}
        variant={isEditMode ? 'white' : 'white-border'}
      >
        <Stack gap="2rem">
          <Text fw={600}>
            See pay period deductions/reimbursements, with focus on changes
            since your last payroll
          </Text>

          <Stack gap="1.5rem">
            {isAdvancedMode && (
              <Grid columns={30}>
                <Grid.Col span={14}>
                  <Controller
                    name="payrollCycle"
                    control={control}
                    render={({ field: { onChange, ...rest } }) => (
                      <PayrollCycleInput
                        {...rest}
                        selectProps={{
                          required: true,
                          onClick: () => sendEvent('click_payroll_cycle'),
                        }}
                        onChange={async (payrollCycle) => {
                          onChange(payrollCycle);
                          handleAutomaticEndDateChange(
                            isAdvancedMode,
                            payrollCycle,
                            selectedStartDate
                          );
                          const isPayPeriodDefined =
                            payrollCycle !== PayrollCycle.SEMI_MONTHLY;
                          handleDefaultIndicateChangesSinceUpdate(
                            isPayPeriodDefined,
                            selectedStartDate
                          );
                          await sendEvent('select_payroll_cycle', {
                            selected_payroll_cycle: payrollCycle,
                          });
                        }}
                      />
                    )}
                  />
                  <FormErrorMessage fieldName="payrollCycle" errors={errors} />
                </Grid.Col>
              </Grid>
            )}

            <Grid columns={30} align="end">
              <Grid.Col span={14}>
                <Controller
                  control={control}
                  name="payPeriodStartDate"
                  render={({ field: { onChange, ...rest } }) => (
                    <DateInput
                      {...rest}
                      label={
                        isAdvancedMode
                          ? 'Pay period'
                          : `Pay period (${getPayrollCycleLabel(employerPayrollCycle, false)})`
                      }
                      placeholder="Start date"
                      required
                      onClick={async () =>
                        await sendEvent('click_pay_period_start_date')
                      }
                      onChange={async (startDate) => {
                        onChange(startDate);
                        handleAutomaticEndDateChange(
                          isAdvancedMode,
                          selectedPayrollCycle,
                          startDate as Date
                        );
                        handleDefaultIndicateChangesSinceUpdate(
                          true,
                          startDate as Date
                        );
                        await sendEvent('select_pay_period_start_date', {
                          start_date: formatDateISO(startDate as Date),
                        });
                      }}
                    />
                  )}
                />
              </Grid.Col>

              <Grid.Col span={2} ta="center">
                <Text mb="md">-</Text>
              </Grid.Col>

              <Grid.Col span={14}>
                <Controller
                  control={control}
                  name="payPeriodEndDate"
                  render={({ field: { onChange, ...rest } }) => {
                    const payrollCycle = isAdvancedMode
                      ? selectedPayrollCycle
                      : employerPayrollCycle;
                    return (
                      <DateInput
                        {...rest}
                        placeholder="End date"
                        required
                        minDate={
                          selectedPayrollCycle
                            ? parseDate(selectedStartDate)
                                .add(11, 'day')
                                .toDate()
                            : undefined
                        }
                        maxDate={
                          selectedStartDate
                            ? parseDate(selectedStartDate)
                                .add(16, 'day')
                                .toDate()
                            : undefined
                        }
                        defaultDate={
                          selectedPayrollCycle
                            ? parseDate(selectedStartDate)
                                .add(13, 'day')
                                .toDate()
                            : undefined
                        }
                        disabled={
                          payrollCycle !== PayrollCycle.SEMI_MONTHLY ||
                          (payrollCycle === PayrollCycle.SEMI_MONTHLY &&
                            !selectedStartDate)
                        }
                        onClick={async () =>
                          await sendEvent('click_pay_period_end_date')
                        }
                        style={(theme) => ({
                          input: {
                            color:
                              payrollCycle !== PayrollCycle.SEMI_MONTHLY
                                ? theme.colors.zorroCoal[9]
                                : theme.colors.zorroGray[4],
                          },
                        })}
                        onChange={async (endDate) => {
                          onChange(endDate);
                          const isPayPeriodDefined = Boolean(selectedStartDate);
                          handleDefaultIndicateChangesSinceUpdate(
                            isPayPeriodDefined,
                            selectedStartDate
                          );
                          await sendEvent('select_pay_period_end_date', {
                            end_date: formatDateISO(endDate as Date),
                          });
                        }}
                      />
                    );
                  }}
                />
              </Grid.Col>
            </Grid>

            {isAdvancedMode && (
              <Grid columns={30}>
                <Grid.Col span={14}>
                  <Controller
                    control={control}
                    key="indicateChangesSince"
                    name="indicateChangesSince"
                    render={({ field: { onChange, ...rest } }) => (
                      <DateInput
                        {...rest}
                        label={
                          <LabelWithTooltip
                            label="Indicate changes since"
                            tooltipLabel="Select a date to include changes made before the start of this report’s pay period"
                            isRequired
                          />
                        }
                        placeholder="Date of last payroll update"
                        maxDate={selectedStartDate || undefined}
                        disabled={!isPayPeriodDefined}
                        onClick={async () =>
                          await sendEvent('click_indicate_changes_since')
                        }
                        onChange={async (indicateChangesSince) => {
                          onChange(indicateChangesSince);
                          await sendEvent(
                            'select_indicate_changes_since_date',
                            {
                              indicate_changes_since: formatDateISO(
                                indicateChangesSince as Date
                              ),
                            }
                          );
                        }}
                      />
                    )}
                  />
                  <FormErrorMessage
                    fieldName="indicateChangesSince"
                    errors={errors}
                  />
                </Grid.Col>
              </Grid>
            )}

            {!isAdvancedMode && (
              <Grid>
                <Grid.Col>
                  <InformationBoxComponent
                    title={
                      <Title order={4}>
                        Results will include changes since the last pay period.
                        For earlier updates or custom cycles, use{' '}
                        <Link
                          href="#use_advanced_mode"
                          onClick={async () => {
                            setValue('reportMode', PayrollReportMode.ADVANCED, {
                              shouldValidate: true,
                              shouldDirty: true,
                            });
                          }}
                        >
                          Advanced mode
                        </Link>
                      </Title>
                    }
                    whiteSpace="pre-line"
                  />
                </Grid.Col>
              </Grid>
            )}
          </Stack>

          {!isEditMode && (
            <div>
              <Button
                size="lg"
                disabled={!isValid}
                onClick={() => onGenerateReportClick(getValues())}
              >
                Generate report
              </Button>
            </div>
          )}
        </Stack>
      </Card>
    </Stack>
  );
};
